import { atom } from "jotai";
import { allServicesAtom, MIN_SEARCH_TERM_LENGTH } from "./atoms";
import { MB_SERVICE_CLASSIFICATION_GROUP } from "../utils/Constants";

export const STATUS_OPTIONS = {
    ALL: "all",
    ACTIVE: "active",
    INACTIVE: "inactive"
};
export const TYPE_OPTIONS = {
    ALL: "all",
    SIMPLE: "One-on-one",
    GROUP: "Group"
};
export const BOOKING_BEHAVIOUR_OPTIONS = {
    ALL: "all",
    PRIVATE: "private",
    PUBLIC: "public",
    WAITLIST: "waitlist"
};

export const selectedServiceIdAtom = atom(null);
export const selectedServiceAtom = atom((get) => {
    const allServices = get(allServicesAtom);
    const selectedServiceId = get(selectedServiceIdAtom);
    if (selectedServiceId === null) {
        return null;
    }
    let service = allServices.find(
        (service) => service.id === selectedServiceId
    );
    if (!service) return null;
    return service;
});
export const openServiceDialogAtom = atom(false);

const tableConfigBaseAtom = atom({
    searchTerm: "",
    statusFilter: STATUS_OPTIONS.ACTIVE,
    typeFilter: TYPE_OPTIONS.ALL,
    bookingBehaviourFilter: BOOKING_BEHAVIOUR_OPTIONS.ALL,
    sort: { sortBy: null, direction: "ASC" }
});

export const tableConfigAtom = atom(
    (get) => get(tableConfigBaseAtom),
    (_get, set, newTableConfig) => {
        set(tableConfigBaseAtom, newTableConfig);
        set(setPageAtom, 0);
    }
);

export const paginationAtom = atom({ page: 0, perPage: 25 });
export const setPageAtom = atom(null, (_get, set, page) => {
    set(paginationAtom, (prev) => ({ ...prev, page }));
});

export const setPerPageAtom = atom(null, (_get, set, perPage) => {
    set(paginationAtom, (prev) => {
        const newPageVal =
            prev.page * perPage > _get(visibleRows).length ? 0 : prev.page;
        return { ...prev, perPage, page: newPageVal };
    });
});

export const formatServicesAtom = atom((get) => {
    const services = get(allServicesAtom);
    const formattedServices = formatServicesHelper(services);
    return [...formattedServices];
});

const formatServicesHelper = (payload) => {
    return payload.map(
        ({
            id,
            active,
            name: serviceName,
            categoryName,
            deleted = false,
            CourseSettings,
            Classification: type,
            isVisible,
            locations,
            behavior: bookingBehaviour,
            minutes: duration,
            packages = {},
            taxexempt: taxExempt,
            price
        }) => {
            if (CourseSettings) {
                try {
                    CourseSettings = JSON.parse(CourseSettings);
                } catch (e) {
                    console.log("error parsing coursesettings");
                }
            }
            return {
                id,
                active,
                serviceName,
                categoryName,
                deleted,
                CourseSettings,
                type:
                    type === MB_SERVICE_CLASSIFICATION_GROUP
                        ? "Group"
                        : "One-on-one",
                typeEnum: type,
                isVisible,
                locations: locations || [],
                bookingBehaviour:
                    bookingBehaviour === "INTERNAL"
                        ? "Private"
                        : bookingBehaviour === "MANUAL"
                          ? "Waitlist"
                          : "Public",
                duration,
                packages,
                taxExempt: taxExempt ? "Yes" : "No",
                price
            };
        }
    );
};

export const visibleRows = atom((get) => {
    const {
        searchTerm,
        statusFilter,
        typeFilter,
        bookingBehaviourFilter,
        sort: { sortBy, direction }
    } = get(tableConfigAtom);

    // Filtering logic
    let filtered = [];
    const allFormattedServices = get(formatServicesAtom);

    filtered =
        searchTerm && searchTerm.length > MIN_SEARCH_TERM_LENGTH
            ? allFormattedServices.filter(
                  ({ serviceName = "", categoryName = "" }) => {
                      const lowerCaseSearchTerm = searchTerm.toLowerCase();
                      const searchTerms = lowerCaseSearchTerm.split(" ");

                      return searchTerms.every(
                          (term) =>
                              serviceName.toLowerCase().includes(term) ||
                              categoryName.toLowerCase().includes(term)
                      );
                  }
              )
            : allFormattedServices;

    filtered =
        filtered.length && statusFilter !== STATUS_OPTIONS.ALL
            ? filtered.filter(({ active }) =>
                  statusFilter === "active" ? active : !active
              )
            : filtered;

    filtered =
        filtered.length && typeFilter !== TYPE_OPTIONS.ALL
            ? filtered.filter(({ type }) =>
                  typeFilter === TYPE_OPTIONS.GROUP
                      ? type === "Group"
                      : type === "One-on-one"
              )
            : filtered;

    filtered =
        filtered.length &&
        bookingBehaviourFilter !== BOOKING_BEHAVIOUR_OPTIONS.ALL
            ? filtered.filter(({ bookingBehaviour }) =>
                  bookingBehaviourFilter === BOOKING_BEHAVIOUR_OPTIONS.PRIVATE
                      ? bookingBehaviour === "Private"
                      : bookingBehaviourFilter ===
                          BOOKING_BEHAVIOUR_OPTIONS.PUBLIC
                        ? bookingBehaviour === "Public"
                        : bookingBehaviour === "Waitlist"
              )
            : filtered;

    // Sorting logic
    let sorted = [];
    switch (sortBy) {
        // All the columns that can be directly compared

        case "capacity":
        case "duration":
        case "price":
            sorted = filtered.sort((a, b) =>
                direction === "ASC"
                    ? a[sortBy] > b[sortBy]
                        ? 1
                        : -1
                    : a[sortBy] < b[sortBy]
                      ? 1
                      : -1
            );
            break;

        case "serviceName":
        case "categoryName":
        case "type":
        case "bookingBehaviour":
        case "taxExempt":
            sorted = filtered.sort((a, b) =>
                direction === "ASC"
                    ? a[sortBy].toLowerCase() > b[sortBy].toLowerCase()
                        ? 1
                        : -1
                    : a[sortBy].toLowerCase() < b[sortBy].toLowerCase()
                      ? 1
                      : -1
            );
            break;

        // All the columns that can be compared by array length
        case "locations":
            sorted = filtered.sort((a, b) =>
                direction === "ASC"
                    ? a[sortBy].length > b[sortBy].length
                        ? 1
                        : -1
                    : a[sortBy].length < b[sortBy].length
                      ? 1
                      : -1
            );
            break;

        default:
            // Default sorting: category name ASC
            sorted = filtered.sort((a, b) => {
                const serviceNameA = a.serviceName.toLowerCase();
                const serviceNameB = b.serviceName.toLowerCase();

                if (serviceNameA < serviceNameB) return -1;
                if (serviceNameA > serviceNameB) return 1;
            });
    }
    return sorted;
});

export const visibleRowsPaginated = atom((get) => {
    const { page, perPage } = get(paginationAtom);
    const visible = get(visibleRows);
    return visible.slice(perPage * page, perPage * (page + 1));
});
